import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { AddWaitlistEntryRequest } from "../contracts/AddWaitlistEntryRequest";
import { SessionResponse } from "../contracts/SessionResponse";
import { WaitlistEntryResponse } from "../contracts/WaitlistEntryResponse";

export const fetchSessionWaitlist = createAsyncThunk<
  { sessionId: string; entries: WaitlistEntryResponse[] },
  string,
  { rejectValue: ApiErrorResponse }
>("waitlist/fetch", async (sessionId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<WaitlistEntryResponse[]>(
      `/bookings/${sessionId}/waitlist`,
    );
    return { sessionId, entries: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const addWaitlistEntry = createAsyncThunk<
  WaitlistEntryResponse,
  { sessionId: string; request: AddWaitlistEntryRequest },
  { rejectValue: ApiErrorResponse }
>("waitlist/add", async ({ sessionId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<WaitlistEntryResponse>(
      `/bookings/${sessionId}/waitlist`,
      request,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const removeWaitlistEntry = createAsyncThunk<
  { sessionId: string; entryId: string },
  { sessionId: string; entryId: string },
  { rejectValue: ApiErrorResponse }
>("waitlist/remove", async ({ sessionId, entryId }, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/bookings/${sessionId}/waitlist/${entryId}`);
    return { sessionId, entryId };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** Moves the queued member onto the roster; returns the updated session (409 when full). */
export const convertWaitlistEntry = createAsyncThunk<
  { sessionId: string; entryId: string; session: SessionResponse },
  { sessionId: string; entryId: string },
  { rejectValue: ApiErrorResponse }
>("waitlist/convert", async ({ sessionId, entryId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SessionResponse>(
      `/bookings/${sessionId}/waitlist/${entryId}/convert`,
    );
    return { sessionId, entryId, session: response.data };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
